import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuietMode } from './QuietModeProvider'
import { useAuth } from './AuthProvider'
import DopaLogo from './DopaLogo'

interface DopaWidgetProps {
  isEnabled?: boolean
}

const DopaWidget = ({ isEnabled = true }: DopaWidgetProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isQuietMode } = useQuietMode()
  const { user } = useAuth()
  
  const [isExpanded, setIsExpanded] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const [showReappearAnimation, setShowReappearAnimation] = useState(false)

  // Hide widget on bot/support screens
  const botScreens = ['/support', '/dopa-listens']
  const isOnBotScreen = botScreens.some(screen => location.pathname.startsWith(screen))

  useEffect(() => {
    if (isOnBotScreen) {
      setIsVisible(false)
      setIsExpanded(false)
    } else {
      // Reappear after leaving bot screen
      const timer = setTimeout(() => {
        setIsVisible(true)
        setShowReappearAnimation(true)
        
        // Clear reappear animation after it completes
        setTimeout(() => setShowReappearAnimation(false), 1000)
      }, 1000)
      
      return () => clearTimeout(timer)
    }
  }, [isOnBotScreen])

  // Don't render if disabled or on bot screens
  if (!isEnabled || isOnBotScreen) return null

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const handleQuickAction = (action: string) => {
    setIsExpanded(false)
    
    switch (action) {
      case 'breathe':
        navigate('/breathing')
        break
      case 'routine':
        navigate('/routines')
        break
      case 'talk':
        navigate('/dopa-listens')
        break
      default:
        break
    }
  }

  const getGreeting = () => {
    if (isQuietMode) {
      return "Hi… I'm still here if you need something gentle."
    }
    
    const greetings = [
      "Want to breathe, talk, or just sit with me?",
      "How can I support you right now?",
      "I'm here whenever you need me.",
      "What feels right for you today?"
    ]
    
    return greetings[Math.floor(Math.random() * greetings.length)]
  }

  const quickActions = [
    {
      id: 'breathe',
      label: 'Breathe with me',
      icon: '🌸',
      action: () => handleQuickAction('breathe')
    },
    {
      id: 'routine',
      label: "What's next in my routine?",
      icon: '🌱',
      action: () => handleQuickAction('routine')
    },
    {
      id: 'talk',
      label: 'Say something kind',
      icon: '💬',
      action: () => handleQuickAction('talk')
    }
  ]

  return (
    <>
      {/* Backdrop for expanded state */}
      {isExpanded && (
        <div 
          className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Widget Container */}
      <div 
        className={`fixed bottom-4 sm:bottom-8 right-4 sm:right-8 z-50 transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        } ${showReappearAnimation ? 'animate-bounce' : ''}`}
      >
        {/* Expanded Panel */}
        {isExpanded && (
          <div 
            className="absolute bottom-20 right-0 bg-sage-25/95 dark:bg-sage-800/95 backdrop-blur-sm rounded-3xl border border-sage-100/50 dark:border-sage-700/50 p-4 sm:p-6 shadow-lg w-80 max-w-[calc(100vw-2rem)]"
            style={{
              animation: 'expandUp 0.3s ease-out'
            }}
          >
            {/* Dopa Greeting */}
            <div className="text-center mb-6">
              <div className="mx-auto mb-4">
                <DopaLogo size="md" className="justify-center" withText={false} />
              </div>
              
              <p className={`text-sage-600 dark:text-sage-300 font-light text-xs sm:text-sm leading-relaxed ${isQuietMode ? 'opacity-70' : ''}`}>
                {getGreeting()}
              </p>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={action.action}
                  className="w-full flex items-center space-x-3 p-3 bg-sage-50/60 dark:bg-sage-800/60 hover:bg-sage-100/60 dark:hover:bg-sage-700/60 
                             text-sage-600 dark:text-sage-300 rounded-2xl border border-sage-100/40 dark:border-sage-700/40 hover:border-sage-200/50 dark:hover:border-sage-600/50
                             transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-sage-100/50 dark:focus:ring-sage-600/50
                             text-left"
                >
                  <span className="text-base sm:text-lg">{action.icon}</span>
                  <span className="font-light text-xs sm:text-sm">{action.label}</span>
                </button>
              ))}
            </div>

            {/* Voice Input Button (if not quiet mode) */}
            {!isQuietMode && (
              <div className="mt-4 pt-4 border-t border-sage-100/30 dark:border-sage-700/30">
                <button
                  onClick={() => handleQuickAction('talk')}
                  className="w-full flex items-center justify-center space-x-2 p-3 bg-peach-50/60 dark:bg-peach-900/30 hover:bg-peach-100/60 dark:hover:bg-peach-800/40 
                             text-peach-600 dark:text-peach-300 rounded-2xl border border-peach-100/40 dark:border-peach-700/30 hover:border-peach-200/50 dark:hover:border-peach-600/40
                             transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-peach-100/50 dark:focus:ring-peach-700/30"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                  <span className="font-light text-xs sm:text-sm">Talk to me</span>
                </button>
              </div>
            )}

            {/* Close hint */}
            <div className="mt-4 text-center">
              <p className="text-sage-400 dark:text-sage-500 text-xs font-light">
                Tap outside to close
              </p>
            </div>
          </div>
        )}

        {/* Floating Button */}
        <button
          onClick={handleToggleExpanded}
          className={`w-16 h-16 rounded-full border border-sage-100/50 dark:border-sage-700/50 shadow-lg
                     transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100/50 dark:focus:ring-sage-600/50
                     ${isQuietMode 
                       ? 'bg-sage-50/30 dark:bg-sage-800/30 opacity-30 hover:opacity-50' 
                       : 'bg-sage-50/80 dark:bg-sage-800/80 hover:bg-sage-100/80 dark:hover:bg-sage-700/80 opacity-90 hover:opacity-100'
                     }
                     ${!isQuietMode && !isExpanded ? 'animate-pulse' : ''}
                     ${isExpanded ? 'scale-110' : 'hover:scale-105'}`}
          title={isQuietMode ? "Dopa is in quiet mode 🌙" : "Talk to Dopa"}
          aria-label="Open Dopa support"
        >
          <div className="flex items-center justify-center">
            {isExpanded ? (
              <svg className="w-5 h-5 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <DopaLogo size="xs" withText={false} />
            )}
          </div>
        </button>

        {/* Reappear message */}
        {showReappearAnimation && !isQuietMode && (
          <div 
            className="absolute bottom-16 right-0 bg-sage-50/90 dark:bg-sage-800/90 backdrop-blur-sm rounded-2xl border border-sage-100/50 dark:border-sage-700/50 p-3 shadow-md max-w-[calc(100vw-2rem)]"
            style={{
              animation: 'fadeInUp 1s ease-out'
            }}
          >
            <p className="text-sage-600 dark:text-sage-300 font-light text-xs whitespace-nowrap">
              Welcome back — I'll stay close.
            </p>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes expandUp {
          from { 
            opacity: 0; 
            transform: translateY(10px) scale(0.95); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }
        
        @keyframes fadeInUp {
          0% { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          50% { 
            opacity: 1; 
            transform: translateY(0); 
          }
          100% { 
            opacity: 0; 
            transform: translateY(-5px); 
          }
        }
      `}</style>
    </>
  )
}

export default DopaWidget